/**
 * Textes du lot L07 (français, langue de référence) : connexions de données d'un projet, lignage,
 * qualité, aperçu par étape d'un job low-code, recherche du catalogue. Branchés dans `fr.ts` ;
 * `donnees.en.ts` porte exactement les mêmes clés (typage + `i18n.test.ts`).
 */
export const connectionsFr = {
  title: 'Connexions de données',
  subtitle:
    'Les bases, serveurs SFTP, API, HDFS et buckets que le projet {{project}} lit et écrit. Aucun mot de passe ici : chaque connexion nomme un secret.',
  subtitle_unnamed:
    'Les bases, serveurs SFTP, API, HDFS et buckets que ce projet lit et écrit. Aucun mot de passe ici : chaque connexion nomme un secret.',
  searchPlaceholder: 'Rechercher une connexion…',
  new: 'Nouvelle connexion',
  newTitle: 'Nouvelle connexion',
  newSubtitle:
    'Les champs suivent le type. Le secret doit être lié au projet et à la destination (hôte:port) : choisissez-en un, ou créez-le ici avec ces deux liaisons.',
  created: 'Connexion {{name}} créée.',
  createdWithSecret: 'Secret {{secret}} créé et lié à {{host}}.',
  saved: 'Connexion enregistrée.',
  empty: {
    title: 'Aucune connexion',
    description:
      'Déclarez une base, un serveur SFTP, une API ou un bucket pour les tester, les explorer et les lire depuis un DAG.',
  },
  columns: {
    name: 'Nom',
    type: 'Type',
    destination: 'Destination',
    secret: 'Secret',
    owner: 'Propriétaire',
    updated: 'Modifiée',
  },
  types: {
    postgresql: 'PostgreSQL',
    mysql: 'MySQL',
    mariadb: 'MariaDB',
    sqlserver: 'SQL Server',
    oracle: 'Oracle',
    s3: 'Bucket (S3)',
    sftp: 'SFTP',
    http: 'API REST',
    hdfs: 'HDFS',
    'hive-metastore': 'Métastore Hive',
  },
  sslModes: {
    default: 'Défaut du pilote',
    disable: 'Sans chiffrement',
    require: 'Chiffré, certificat non vérifié',
    verify: 'Chiffré et vérifié',
  },
  authTypes: {
    none: 'Aucune',
    bearer: 'Jeton porteur (Bearer)',
    basic: 'Basique (utilisateur et mot de passe)',
    header: 'En-tête personnalisé',
  },
  secretModes: {
    none: 'Sans secret',
    existing: 'Choisir un secret du projet',
    new: 'Créer le secret ici',
  },
  fields: {
    name: 'Nom',
    nameHelp: 'Minuscules, chiffres, tirets et soulignés : c’est le repère de la connexion dans les DAG.',
    type: 'Type',
    host: 'Hôte',
    port: 'Port',
    portHelp: 'Vide : le port par défaut du type (5432, 3306, 1433, 1521, 22, 9870, 9083).',
    database: 'Base',
    databaseHelp: 'Oracle : le nom de service.',
    schema: 'Schéma',
    username: 'Utilisateur',
    sslMode: 'Chiffrement',
    sslModeHelp: 'Oracle n’accepte que « sans chiffrement ».',
    rpcPort: 'Port RPC',
    catalogSync: 'Publier au catalogue',
    catalogSyncHelp:
      'Les tables du métastore entrent dans la couche « hive » du catalogue de l’organisation (lisible hors du projet). Demande le droit de créer au catalogue.',
    rpcPortHelp: 'Utilisé par Spark (défaut : 8020).',
    datanodes: 'Datanodes',
    datanodesHelp:
      'Hôtes des datanodes que les runs peuvent joindre (ports 9864 et 9866), séparés par des virgules. Vide : le namenode seul.',
    kerberosPrincipal: 'Principal Kerberos',
    kerberosPrincipalHelp:
      'Vide : authentification simple. Renseigné : la connexion s’authentifie par Kerberos, et son secret est la keytab encodée en base64 (base64 -w0 fichier.keytab).',
    kerberosKdcs: 'KDC',
    kerberosKdcsHelp: 'hôte[:port] des KDC du realm, séparés par des virgules (5 au plus, TCP).',
    kerberosServicePrincipal: 'Principal du namenode',
    kerberosServicePrincipalHelp:
      'Vide : nn/_HOST@<realm du principal> (HDFS), hive/_HOST@<realm> (métastore).',
    hostKey: 'Empreinte de la clé d’hôte',
    hostKeyHelp:
      'Empreinte OpenSSH SHA256. Vide : le test rapporte celle du serveur sans s’authentifier, et vous l’épinglez.',
    bucket: 'Bucket',
    baseUrl: 'URL de base',
    baseUrlHelp: 'http(s)://hôte[:port], sans chemin.',
    authType: 'Authentification',
    authHeader: 'Nom de l’en-tête',
    path: 'Chemin',
    pathHelp: 'Dossier ou préfixe de base ; pour une API, le chemin d’aperçu par défaut.',
    secretMode: 'Secret',
    secretModeHelp: 'Mot de passe, clé ou jeton : jamais saisi dans la connexion, toujours dans un secret.',
    secretId: 'Secret du projet',
    secretIdHelp:
      'Seuls les secrets liés à ce projet sont proposés ; leur destination doit être celle de la connexion.',
    secretName: 'Nom du nouveau secret',
    secretNameHelp: 'Lettres, chiffres, « _ », « . » et « - ». Il sera lié au projet et à la destination.',
    secretValue: 'Valeur du secret',
    secretValueHelp: 'Envoyée une fois, scellée par la plateforme, jamais réaffichée.',
    description: 'Description',
  },
  errors: {
    name: 'Minuscules, chiffres, « - » et « _ », 63 caractères au plus.',
    baseUrl: 'Une URL http(s)://hôte[:port], sans chemin.',
    secretHost: 'Ce secret est lié à {{host}}, la connexion vise {{destination}}.',
    secretName: 'Un nom de secret : lettres, chiffres, « _ », « . » et « - ».',
    noDestination: 'Renseignez d’abord l’hôte (ou l’URL) : le secret est lié à cette destination.',
    principal: 'Un principal Kerberos : nom[/instance]@REALM.',
    notKeytab: 'Ce secret n’est pas une keytab : une connexion Kerberos demande un secret créé comme keytab.',
    keytab: 'Ce secret est une keytab Kerberos : il ne sert qu’à une connexion Kerberos.',
    hosts: 'Des hôtes hôte[:port] séparés par des virgules, {{max}} au plus.',
  },
  detail: {
    tabs: {
      summary: 'Résumé',
      test: 'Tester',
      explore: 'Explorer',
      definition: 'Définition',
    },
    destination: 'Destination',
    noSecret: 'Sans secret',
    hostKeyMissing: 'Aucune empreinte épinglée',
    delete: 'Supprimer la connexion',
    confirmDelete:
      'Supprimer la connexion {{name}} ? Les DAG qui en ont recopié les champs ne changent pas ; le secret n’est pas supprimé.',
    deleted: 'Connexion {{name}} supprimée.',
  },
  test: {
    hint: 'La plateforme se connecte depuis graal-api avec le secret lié, et ne lit rien.',
    status: 'Résultat',
    run: 'Tester la connexion',
    running: 'Test en cours…',
    ok: 'Joignable',
    failed: 'Injoignable',
    latency: 'Latence',
    latencyValue: '{{ms}} ms',
    server: 'Serveur',
    message: 'Réponse',
    observedKey: 'Empreinte présentée par le serveur',
    pinHint:
      'Aucune empreinte n’est épinglée : le test s’est arrêté après l’échange de clés, sans s’authentifier. Vérifiez cette empreinte auprès de l’administrateur du serveur avant de l’épingler.',
    mismatch: 'L’empreinte présentée ne correspond PAS à celle épinglée ({{pinned}}).',
    pin: 'Épingler cette empreinte',
    pinned: 'Empreinte épinglée. Relancez le test pour vous authentifier.',
    idle: 'Le résultat du dernier test s’affiche ici.',
  },
  explore: {
    title: 'Objets',
    root: 'Racine',
    up: 'Dossier parent',
    columns: {
      name: 'Nom',
      kind: 'Nature',
      size: 'Taille',
    },
    kinds: {
      table: 'Table',
      database: 'Base',
      view: 'Vue',
      file: 'Fichier',
      directory: 'Dossier',
    },
    empty: { title: 'Rien ici', description: 'La connexion ne rend aucun objet à cet endroit.' },
    schema: 'Colonnes de {{object}}',
    schemaColumns: { name: 'Colonne', type: 'Type', nullable: 'Nullable' },
    preview: 'Premières lignes',
    previewMeta_one: '{{count}} ligne · {{ms}} ms',
    previewMeta_other: '{{count}} lignes · {{ms}} ms',
    truncated: 'Coupé à {{limit}} lignes',
    format: 'Format : {{format}}',
    noRows: { title: 'Aucune ligne', description: 'L’objet ne contient aucune ligne lisible.' },
    back: 'Retour à la liste',
  },
} as const;

export const lineageFr = {
  title: 'Lignage',
  subtitle:
    'Les jeux de données lus et écrits par les runs de {{tenant}}, relus des événements OpenLineage. Ouvrez-en un pour voir d’où il vient et où il va.',
  searchPlaceholder: 'Namespace, nom ou champ…',
  serverSearch: 'Rechercher dans le lignage',
  columns: {
    dataset: 'Jeu de données',
    namespace: 'Namespace',
    fields: 'Champs',
    quality: 'Qualité',
    lastSeen: 'Vu',
    job: 'Dernier job',
  },
  fieldsCount_one: '{{count}} champ',
  fieldsCount_other: '{{count}} champs',
  empty: {
    title: 'Aucun jeu de données',
    description: 'Le lignage se remplit quand un run émet ses événements OpenLineage (job low-code, Spark).',
  },
  graph: {
    title: 'Lignage de {{name}}',
    subtitle: 'Jeux et jobs reliés à {{namespace}}, sur {{depth}} sauts dans chaque sens.',
    depth: 'Profondeur',
    back: 'Lignage',
    canvas: 'Graphe du lignage',
    kinds: { dataset: 'Jeu', job: 'Job' },
    nodeCount: 'Jeux : {{datasets}} · jobs : {{jobs}}',
    columnsTitle: 'Lignage colonne',
    columns: {
      source: 'Source',
      target: 'Cible',
      transformation: 'Transformation',
    },
    noColumns: {
      title: 'Pas de lignage colonne',
      description: 'Les runs de ces jobs n’ont pas émis la facette columnLineage.',
    },
    openDataset: 'Voir ce jeu',
    openJob: 'Ouvrir le job',
    notFound: 'Aucun run n’a jamais lu ni écrit ce jeu de données.',
  },
  run: {
    inputs: 'Jeux lus',
    outputs: 'Jeux écrits',
    columns: { dataset: 'Jeu de données', namespace: 'Namespace', fields: 'Champs' },
    viewGraph: 'Voir le graphe',
    events_one: '{{count}} événement OpenLineage',
    events_other: '{{count}} événements OpenLineage',
    empty: {
      title: 'Aucun événement de lignage',
      description: 'Ce run n’a pas émis d’événement OpenLineage (GRAAL_OPENLINEAGE).',
    },
    noInputs: { title: 'Aucun jeu lu' },
    noOutputs: { title: 'Aucun jeu écrit' },
  },
} as const;

export const qualityFr = {
  statuses: {
    passed: 'Conforme',
    warning: 'Avertissement',
    failed: 'Non conforme',
    unknown: 'Non évaluée',
  },
  severities: { error: 'Bloquant', warning: 'Avertissement', info: 'Information' },
  label: 'Qualité',
  run: {
    title: 'Contrôles qualité',
    summary_one: '{{count}} contrôle en échec sur {{total}}',
    summary_other: '{{count}} contrôles en échec sur {{total}}',
    created: 'Rapport du {{date}}',
    columns: {
      status: 'Résultat',
      check: 'Contrôle',
      column: 'Colonne',
      severity: 'Gravité',
      observed: 'Observé',
      expected: 'Attendu',
      message: 'Message',
      task: 'Étape',
    },
    checkStatuses: { passed: 'Réussi', failed: 'Échec' },
    empty: {
      title: 'Pas de rapport de qualité',
      description: 'Ce run n’a pas exécuté de tâche « Contrôles qualité » (GRAAL_QUALITY_REPORT).',
    },
  },
} as const;

export const previewFr = {
  title: 'Aperçu d’une étape',
  hint: 'Lance un run éphémère réduit à l’étape et à ses ancêtres, sur un échantillon ; le job n’est pas modifié. Une étape d’écriture se prévisualise par ce qu’elle écrirait.',
  fields: {
    task: 'Étape du DAG',
    limit: 'Lignes au plus',
    limitHelp: 'De 1 à 500.',
  },
  run: 'Lancer l’aperçu',
  statuses: {
    pending: 'En file',
    running: 'En cours',
    complete: 'Terminé',
    failed: 'Échec',
  },
  runId: 'Run',
  result: 'Résultat de {{task}}',
  meta_one: '{{count}} ligne',
  meta_other: '{{count}} lignes',
  truncated: 'Échantillon coupé',
  failed: 'L’aperçu a échoué : {{error}}',
  waiting: 'Le run de l’aperçu démarre ; le résultat s’affiche dès qu’il est prêt.',
  idle: { title: 'Aucun aperçu', description: 'Choisissez une étape puis lancez l’aperçu.' },
  noDefinition: {
    title: 'Pas de DAG',
    description: 'Ce job low-code n’a pas de définition : il n’y a pas d’étape à prévisualiser.',
  },
  empty: { title: 'Aucune ligne', description: 'L’étape ne produit aucune ligne sur cet échantillon.' },
} as const;

export const catalogSearchFr = {
  placeholder: 'Rechercher une table, une description, un tag, un terme…',
  submit: 'Rechercher',
  columnPlaceholder: 'Rechercher par nom de colonne…',
  columnSubmit: 'Colonne',
  title: 'Tables trouvées',
  meta_one: '{{count}} table pour « {{search}} »',
  meta_other: '{{count}} tables pour « {{search}} »',
  metaFacets_one: '{{count}} table pour ces filtres',
  metaFacets_other: '{{count}} tables pour ces filtres',
  metaTotal: '({{total}} au total, affinez la recherche)',
  clear: 'Effacer',
  includeDropped: 'Tables disparues du moteur',
  facets: {
    all: '{{facet}} : tous',
    option: '{{value}} ({{count}})',
    layer: 'Catalogue',
    format: 'Format',
    tag: 'Tag',
    owner: 'Propriétaire',
    certification: 'Certification',
    quality: 'Qualité',
    classification: 'Classification',
  },
  columns: {
    table: 'Table',
    location: 'Catalogue / base',
    owner: 'Propriétaire',
    certification: 'Certification',
    tags: 'Tags',
    fields: 'Champs trouvés',
    quality: 'Qualité',
  },
  empty: { title: 'Aucune table', description: 'Aucune table lisible ne correspond à cette recherche.' },
  viewLineage: 'Voir le lignage',
} as const;
